import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import React, { useEffect, useMemo, useState, useContext } from "react";
import "./home.scss";
import { computeWeekSummary } from "../../utils/weekSummary";
import { formatMinutesToHoursMinutes } from "../../utils/productionScheduling";
import { MASS_UNITS } from "../../constants";
import { onSnapshot, collection } from "firebase/firestore";
import { db } from "../../firebase";
import { OrganizationsContext } from "../../context/OrganizationsContext";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

const Home = () => {
  const [ingredients, setIngredients] = useState([]); // kept (used elsewhere / future)
  const [doughs, setDoughs] = useState([]); // kept
  const [products, setProducts] = useState([]);
  const [productions, setProductions] = useState([]);
  const [lines, setLines] = useState([]);

  const { selected } = useContext(OrganizationsContext);
  const organizationId = JSON.parse(localStorage.getItem("organizations"))
    .selected.id;

  const [selectedProductId, setSelectedProductId] = useState("");
  const [productChartData, setProductChartData] = useState([]);
  const [doughChartData, setDoughChartData] = useState([]);

  // --- Date Range ---
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);

  const [startDate, setStartDate] = useState(
    lastWeek.toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  // --- Week helpers (Monday -> Sunday) ---
  const toISODate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseISODateLocal = (iso) => {
    // Avoid Date("YYYY-MM-DD") timezone shifting by parsing as local date
    const [y, m, d] = String(iso).split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  };

  const formatMMDDYYYY = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const dayLabel = (d) => {
    const names = [
      "SUNDAY",
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
    ];
    return `${names[d.getDay()]} ${formatMMDDYYYY(d)}`;
  };

  const getWeekStartMonday = (date = new Date()) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0=Sun
    const mondayOffset = (day + 6) % 7; // Mon=0
    d.setDate(d.getDate() - mondayOffset);
    return d;
  };

  const weekStart = useMemo(() => getWeekStartMonday(today), [today]);
  const weekDates = useMemo(() => {
    // Always produce 7 days Mon..Sun (even if no production)
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      d.setHours(0, 0, 0, 0);
      return d;
    });
  }, [weekStart]);

  // generic fetch
  const fetchItems = (collectionName, stateUpdater) => {
    return onSnapshot(
      collection(db, "organizations", organizationId, collectionName),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: collectionName + "/" + doc.id,
          collection: collectionName,
          ...doc.data(),
        }));
        stateUpdater(data);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  useEffect(() => {
    const unsubIngredients = fetchItems("ingredients", setIngredients);
    const unsubDoughs = fetchItems("doughs", setDoughs);
    const unsubProducts = fetchItems("products", setProducts);
    const unsubLines = fetchItems("lines", setLines);
    const unsubProductions = fetchItems("productions", setProductions);

    return () => {
      unsubIngredients();
      unsubDoughs();
      unsubProducts();
      unsubLines();
      unsubProductions();
    };
  }, [organizationId]);

  // --- Product chart (pieces only, no scrap) ---
  useEffect(() => {
    if (!selectedProductId || productions.length === 0) return;

    const dataByDate = {};

    productions.forEach((prod) => {
      const date = prod.date;
      if (!date) return;

      // check date range
      if (date < startDate || date > endDate) return;

      if (prod.lineProductQuantities) {
        Object.values(prod.lineProductQuantities).forEach((lineMap) => {
          Object.values(lineMap).forEach((productMap) => {
            if (productMap[selectedProductId]) {
              const { quantity, unit } = productMap[selectedProductId];
              const pieces =
                (Number(quantity) || 0) *
                (unit?.pieceCount ? parseInt(unit.pieceCount, 10) : 1);
              if (!dataByDate[date]) dataByDate[date] = 0;
              dataByDate[date] += pieces;
            }
          });
        });
      }
    });

    const formatted = Object.entries(dataByDate)
      .map(([date, total]) => ({
        date,
        pieces: total,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    setProductChartData(formatted);
  }, [selectedProductId, productions, startDate, endDate]);

  // --- Dough chart (weight in lbs, including scrap if rework) ---
  useEffect(() => {
    if (productions.length === 0) return;

    const dataByDate = {};

    productions.forEach((prod) => {
      const date = prod.date;
      if (!date) return;
      if (date < startDate || date > endDate) return;

      if (prod.lineProductQuantities) {
        Object.entries(prod.lineProductQuantities).forEach(([_, lineMap]) => {
          Object.entries(lineMap).forEach(([lineId, productMap]) => {
            Object.entries(productMap).forEach(([productId, info]) => {
              const product = products.find((p) => p.id === productId);
              const line = lines.find((l) => l.id === lineId);
              if (!product || !line) return;

              const ingredientsWeight = (product.ingredients || [])
                .filter((ingredient) =>
                  Object.keys(ingredient)[0].startsWith("doughs/")
                )
                .reduce((sum, ingredient) => {
                  const key = Object.keys(ingredient)[0];
                  const weight = parseFloat(ingredient[key]) || 0;
                  return sum + weight;
                }, 0);

              const pieces =
                (Number(info.quantity) || 0) *
                (info.unit?.pieceCount
                  ? parseInt(info.unit.pieceCount, 10)
                  : 1);

              let scrapFactor = 0;
              if (info.rework && line.products) {
                const prodLineInfo = line.products.find((p) => p[productId]);
                if (prodLineInfo && prodLineInfo[productId]?.scrapPercent) {
                  scrapFactor =
                    parseFloat(prodLineInfo[productId].scrapPercent) / 100;
                }
              }

              const totalWeightGrams = ingredientsWeight * pieces;
              const totalWeightWithScrap = totalWeightGrams * (1 + scrapFactor);
              const totalWeightLbs = totalWeightWithScrap / 454;

              if (!dataByDate[date]) dataByDate[date] = 0;
              dataByDate[date] += totalWeightLbs;
            });
          });
        });
      }
    });

    const formatted = Object.entries(dataByDate)
      .map(([date, totalWeight]) => ({
        date,
        totalWeight: parseFloat(totalWeight.toFixed(2)),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    setDoughChartData(formatted);
  }, [productions, products, lines, startDate, endDate]);

  // --- Weekly summary (Mon..Sun, always 7 sections) ---
  // Aggregates by productId per day so each product appears exactly once
  // even when multiple production documents exist for the same date or when
  // the same product is assigned to more than one line.
  const weekSummary = useMemo(
    () =>
      computeWeekSummary({
        productions,
        products,
        weekDates,
        toISODate,
        parseISODateLocal,
        doughs,
        lines,
      }),
    [productions, products, weekDates, doughs, lines]
  );

  return (
    <div className="home">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />
        {selected ? <h2>{selected.name} Dashboard</h2> : null}

        {/* Date Range Selector */}
        <div className="date-range">
          <label>
            From:{" "}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label>
            To:{" "}
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
        </div>

        <div className="dashboard-section" style={{ padding: "2px" }}>
          <h3>Product Production Over Time</h3>
          <select
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
          >
            <option value="">Select a product</option>
            {products
              .slice()
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.id}
                </option>
              ))}
          </select>

          {productChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={productChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="pieces" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p>Select a product to see production history.</p>
          )}
        </div>

        <div className="dashboard-section" style={{ padding: "2px" }}>
          <h3>Total Dough Processed (lbs)</h3>
          {doughChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={doughChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="totalWeight" stroke="#82ca9d" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p>No production data in selected range.</p>
          )}
        </div>

        {/* Weekly Summary Table (Mon..Sun) */}
        <div className="dashboard-section" style={{ marginTop: "20px" }}>
          <h3>
            Current Week Summary{" "}
            <span style={{ fontWeight: 400, color: "#666", fontSize: 14 }}>
              (Mon–Sun)
            </span>
          </h3>

          {weekDates.map((d) => {
            const iso = toISODate(d);
            const items = weekSummary[iso] || [];

            return (
              <div key={iso} style={{ marginBottom: "14px" }}>
                <h4>{dayLabel(d)}</h4>

                {items.length === 0 ? (
                  <div style={{ color: "#666", padding: "6px 0 10px 0" }}>
                    No production scheduled.
                  </div>
                ) : (
                  <table className="week-summary-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity (pieces)</th>
                        <th>Racks Needed</th>
                        <th>Boards Needed</th>
                        <th>Mixes</th>
                        <th>Production Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.name}</td>
                          <td>
                            {item.quantity} {item.unitName} ({item.pieces} pieces)
                          </td>
                          <td>{item.racksNeeded !== null ? item.racksNeeded : "-"}</td>
                          <td>{item.boardsNeeded !== null ? item.boardsNeeded : "-"}</td>
                          <td>
                            {item.mixCount !== null && item.mixWeightGrams !== null
                              ? `${item.mixCount} @ ${Math.round(item.mixWeightGrams / MASS_UNITS[0].amount)}lbs`
                              : "-"}
                          </td>
                          <td>
                            {item.productionMinutes !== null
                              ? formatMinutesToHoursMinutes(item.productionMinutes)
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Home;